import {
  OntologyFieldDefinition,
  OntologyValueType,
} from 'features/dynamicOntology'
import { getValueWithDefault } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  InputField,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormSection } from 'features/libra/sections/sectionTypes'
import { RecursivePropertyView } from 'features/objectViews'
import pick from 'lodash/pick'
import pickBy from 'lodash/pickBy'
import { Dispatch, FC, SetStateAction, useMemo } from 'react'
import styled from 'styled-components'

import { ExternalValueLookup } from './ExternalValueLookup'

type FieldValuesType = Record<string, unknown>

interface ConditionalFormSectionProps {
  config: FormSection
  ontologyFields: Record<string, OntologyField>
  virtualObject: FieldValuesType
  setVirtualObject: Dispatch<SetStateAction<FieldValuesType>>
}

export function createVirtualObject(
  fieldValues: FieldValuesType,
  ontologyFields: Record<string, InputField>
): FieldValuesType {
  return Object.fromEntries(
    Object.entries(ontologyFields).map(([key, fieldDefinition]) => [
      key.split('.')[0],
      getValueWithDefault(fieldValues?.[key], fieldDefinition),
    ])
  )
}

export const ConditionalFormSectionView: FC<ConditionalFormSectionProps> = ({
  config,
  ontologyFields,
  virtualObject,
  setVirtualObject,
}) => {
  const sectionFieldValues = virtualObject ?? {}

  // // Filter to only INPUT fields that are in this section
  const filteredOntologyFields = useMemo<Record<string, InputField>>(
    () =>
      pickBy(
        pick(ontologyFields, config.fields),
        (field) => field?.category === FieldCategory.INPUT
      ) as Record<string, InputField>,
    [config.fields, ontologyFields]
  )

  const viewFields = useMemo<
    Record<string, [string, OntologyFieldDefinition]>
  >(() => {
    return Object.fromEntries(
      Object.entries(filteredOntologyFields).map(([key, fieldDef]) => [
        key.split('.')[0],
        [key, fieldDef],
      ])
    )
  }, [filteredOntologyFields])

  return (
    <SectionContainer>
      <RecursivePropertyView
        object={
          createVirtualObject(
            sectionFieldValues,
            filteredOntologyFields
          ) as OntologyValueType
        }
        setObject={
          setVirtualObject as Dispatch<
            SetStateAction<OntologyValueType | undefined>
          >
        }
        ontologyFields={viewFields}
        label={undefined}
        leafActions={({ field, setObject }) => {
          return setObject ? (
            <ExternalValueLookup field={field} setValue={setObject} />
          ) : undefined
        }}
      />
    </SectionContainer>
  )
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`
